/**
 * Chat Feature Constants
 *
 * Centralized constants for the Chat feature to eliminate magic strings and numbers.
 * This improves maintainability, reduces typos, and makes the codebase more readable.
 *
 * @module ChatConstants
 */

// =============================================================================
// Storage Keys
// =============================================================================

/**
 * Storage key patterns for sessionStorage and localStorage
 */
export const STORAGE_KEYS = {
    /**
     * Key for storing correlation IDs in sessionStorage
     * @param conversationId - The conversation ID
     * @returns Storage key string
     */
    CORRELATION: (conversationId: string) => `correlation:${conversationId}`,

    /**
     * Key for storing user messages in sessionStorage
     * @param conversationId - The conversation ID
     * @returns Storage key string
     */
    USER_MESSAGE: (conversationId: string) => `userMessage:${conversationId}`,

    /**
     * Key for storing tabs state in localStorage
     */
    TABS: 'tabs_by_conversation',
} as const;

// =============================================================================
// Tab Management
// =============================================================================

/**
 * Configuration for tab management
 */
export const TAB_CONFIG = {
    /**
     * Maximum number of tabs allowed per conversation
     */
    MAX_PER_CONVERSATION: 10,

    /**
     * Maximum total tabs across all conversations
     */
    MAX_TOTAL: 50,

    /**
     * Maximum storage size for tabs in KB
     */
    MAX_STORAGE_SIZE_KB: 100,

    /**
     * Number of days after which unused tabs are cleaned up
     */
    CLEANUP_AFTER_DAYS: 7,

    /**
     * Storage version for migration purposes
     */
    STORAGE_VERSION: 1,

    /**
     * Whether to enable automatic cleanup of stale tabs
     */
    ENABLE_AUTO_CLEANUP: true,

    /**
     * Special tab key for the home/cohort view
     */
    HOME_TAB_KEY: 'home',
} as const;

// =============================================================================
// Stream Events
// =============================================================================

/**
 * Server-Sent Events (SSE) stream event types
 */
export const STREAM_EVENTS = {
    /**
     * Regular message chunk from the stream
     */
    MESSAGE: 'message',

    /**
     * Stream completed successfully
     */
    DONE: 'done',

    /**
     * Error occurred during streaming
     */
    ERROR: 'error',

    /**
     * Stream was aborted/cancelled
     */
    ABORT: 'abort',
} as const;

/**
 * Type for stream event names
 */
export type StreamEventType = (typeof STREAM_EVENTS)[keyof typeof STREAM_EVENTS];

// =============================================================================
// Assistant Message Stages
// =============================================================================

/**
 * Stages of assistant message processing
 */
export const MESSAGE_STAGES = {
    /**
     * Final response stage
     */
    FINAL_RESPONSE: 'final_response',

    /**
     * Intermediate processing stage
     */
    PROCESSING: 'processing',

    /**
     * Thinking/analyzing stage
     */
    THINKING: 'thinking',
} as const;

/**
 * Type for message stages
 */
export type MessageStage = (typeof MESSAGE_STAGES)[keyof typeof MESSAGE_STAGES];

// =============================================================================
// Traffic Control
// =============================================================================

/**
 * Traffic control states for message flow
 */
export const TRAFFIC_STATES = {
    /**
     * Allow new messages
     */
    ALLOW: 'allow',

    /**
     * Stop/block new messages
     */
    STOP: 'stop',
} as const;

/**
 * Type for traffic states
 */
export type TrafficState = (typeof TRAFFIC_STATES)[keyof typeof TRAFFIC_STATES];

// =============================================================================
// Debounce Delays
// =============================================================================

/**
 * Debounce delays in milliseconds
 */
export const DEBOUNCE_DELAYS = {
    /**
     * Search input debounce delay
     */
    SEARCH: 500,

    /**
     * Sort operation debounce delay
     */
    SORT: 300,

    /**
     * Tab access tracking throttle
     */
    TAB_ACCESS_THROTTLE: 1000,

    /**
     * Quick action delay (e.g., tooltip, hover states)
     */
    QUICK: 200,
} as const;

// =============================================================================
// Pagination
// =============================================================================

/**
 * Default pagination configuration
 */
export const PAGINATION = {
    /**
     * Default page number (1-indexed)
     */
    DEFAULT_PAGE: 1,

    /**
     * Default page size for chat history
     */
    DEFAULT_PAGE_SIZE: 100,

    /**
     * Page size for table results
     */
    TABLE_PAGE_SIZE: 10,
} as const;

// =============================================================================
// API Endpoints
// =============================================================================

/**
 * Chat API endpoint paths (relative to base URL)
 */
export const CHAT_ENDPOINTS = {
    /**
     * Get conversation history
     */
    CONVERSATION: (conversationId: string) => `conversation/${conversationId}`,

    /**
     * Get patient-specific conversation
     */
    PATIENT_CONVERSATION: (conversationId: string, patientId: string) => `conversation/${conversationId}/${patientId}`,

    /**
     * Get conversation samples
     */
    SAMPLES: 'conversation/samples',

    /**
     * Send message endpoint
     */
    SEND_MESSAGE: 'conversation/send',

    /**
     * Stream conversation endpoint
     */
    STREAM: (conversationId: string, correlationId: string) => `conversation/stream/${conversationId}/${correlationId}`,

    /**
     * Get correlation status
     */
    CORRELATION_STATUS: (correlationId: string) => `conversation/correlation/${correlationId}`,
} as const;

// =============================================================================
// Tab Types
// =============================================================================

/**
 * Types of tabs that can be opened
 */
export const TAB_TYPES = {
    /**
     * Home/cohort tab
     */
    HOME: 'home',

    /**
     * Patient-specific tab
     */
    PATIENT: 'patient',
} as const;

/**
 * Type for tab types
 */
export type TabType = (typeof TAB_TYPES)[keyof typeof TAB_TYPES];

// =============================================================================
// Table Editor Tabs
// =============================================================================

/**
 * Tab keys for the TableEditorPane component
 */
export const TABLE_EDITOR_TABS = {
    /**
     * Query editor tab
     */
    EDITOR: 'editor',

    /**
     * Results display tab
     */
    RESULT: 'result',
} as const;

/**
 * Type for table editor tab keys
 */
export type TableEditorTab = (typeof TABLE_EDITOR_TABS)[keyof typeof TABLE_EDITOR_TABS];

// =============================================================================
// Error Severity Levels
// =============================================================================

/**
 * Error severity levels for logging and handling
 * (This may be duplicated from utils/errorHandling - consider consolidating)
 */
export const ERROR_SEVERITY = {
    /**
     * Critical error requiring immediate attention
     */
    CRITICAL: 'critical',

    /**
     * High priority error
     */
    HIGH: 'high',

    /**
     * Warning level
     */
    WARNING: 'warning',

    /**
     * Informational only
     */
    INFO: 'info',
} as const;

/**
 * Type for error severity
 */
export type ErrorSeverity = (typeof ERROR_SEVERITY)[keyof typeof ERROR_SEVERITY];

// =============================================================================
// Retry Configuration
// =============================================================================

/**
 * Retry configuration for failed operations
 */
export const RETRY_CONFIG = {
    /**
     * Maximum number of retry attempts
     */
    MAX_RETRIES: 3,

    /**
     * Base delay between retries in milliseconds (exponential backoff)
     */
    BASE_DELAY_MS: 1000,

    /**
     * Maximum delay between retries in milliseconds
     */
    MAX_DELAY_MS: 5000,
} as const;

// =============================================================================
// Time Formats
// =============================================================================

/**
 * Time-related constants
 */
export const TIME = {
    /**
     * Milliseconds in one second
     */
    SECOND_MS: 1000,

    /**
     * Milliseconds in one minute
     */
    MINUTE_MS: 60 * 1000,

    /**
     * Milliseconds in one hour
     */
    HOUR_MS: 60 * 60 * 1000,

    /**
     * Milliseconds in one day
     */
    DAY_MS: 24 * 60 * 60 * 1000,
} as const;

// =============================================================================
// Exports
// =============================================================================

/**
 * All chat constants grouped together for convenience
 */
export const CHAT_CONSTANTS = {
    STORAGE_KEYS,
    TAB_CONFIG,
    STREAM_EVENTS,
    MESSAGE_STAGES,
    TRAFFIC_STATES,
    DEBOUNCE_DELAYS,
    PAGINATION,
    CHAT_ENDPOINTS,
    TAB_TYPES,
    TABLE_EDITOR_TABS,
    ERROR_SEVERITY,
    RETRY_CONFIG,
    TIME,
} as const;

/**
 * Type for the entire constants object
 */
export type ChatConstants = typeof CHAT_CONSTANTS;

// =============================================================================
// Backward Compatibility Exports
// =============================================================================

/**
 * @deprecated Use STORAGE_KEYS.CORRELATION instead
 */
export const CORRELATION_KEY = STORAGE_KEYS.CORRELATION;

/**
 * @deprecated Use STORAGE_KEYS.USER_MESSAGE instead
 */
export const USER_MESSAGE_KEY = STORAGE_KEYS.USER_MESSAGE;
